import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Modal } from '@/components/ui/modal';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { showToast } from '@/components/ui/sonner';
import { Switch } from '@/components/ui/switch';
import useLogout from '@/hooks/useLogout';
import { handleError } from '@/lib/utils';
import { authState } from '@/vars/auth';
import { useMutation, useReactiveVar } from '@apollo/client';
import { useState } from 'react';
import { ACCOUNT_DELETE } from '../GraphQl/mutation';

interface DeleteAccountProps {
  open: boolean;
  handleClose: () => void;
}

const DeleteAccount: React.FC<DeleteAccountProps> = ({ open, handleClose }) => {
  const { onLogout } = useLogout();
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState<string>('');
  const { userDetails } = useReactiveVar(authState);
  const [error, setError] = useState<'type1' | 'type2' | 'type3' | ''>('');

  const [deleteAccount, { loading }] = useMutation(ACCOUNT_DELETE, {
    onCompleted: () => {
      showToast({ message: 'Your account deleted successfully', variant: 'success' });
      onLogout();
    },
    onError: handleError,
  });

  const handleReasonChange = (value: string) => {
    if (!confirm && value) {
      setError('type2');
    }
    setReason(value);
  };

  const Footer = () => {
    const handleDelete = () => {
      if (!reason && !confirm) {
        setError('type3');
      } else if (!reason) {
        setError('type1');
      } else if (!confirm) {
        setError('type2');
      }
      if (!loading && reason && confirm && !error) {
        deleteAccount({
          variables: {
            id: userDetails?._id,
            reason,
          },
        });
      }
    };

    return (
      <div className='w-full flex items-center gap-4 pt-4 px-7 pb-7'>
        <Button
          id='delete-cancel-btn'
          variant='secondary'
          className='w-full'
          type='button'
          onClick={() => handleClose()}
        >
          I want to keep my account
        </Button>
        <Button
          id='delete-confirm-btn'
          loading={loading}
          className='w-full bg-[#C40C2F] hover:bg-[#822738]'
          variant='destructive'
          type='button'
          onClick={handleDelete}
        >
          Delete account
        </Button>
      </div>
    );
  };
  return (
    <Modal
      title='Delete Account'
      open={open}
      onClose={handleClose}
      footer={<Footer />}
      className='bg-[#151525]'
    >
      <div className='space-y-6 overflow-auto max-h-[calc(100vh-210px)] px-7 py-3'>
        <div className='flex flex-col gap-2'>
          <h3 className='font-semibold'>Deleting Your Account is Permanent</h3>
          <p className='text-sm text-[#C5C8D0]'>
            When you delete your account, all your account information, profile details, balance,
            and subscribed sessions will be permanently deleted. Please ensure that you use any
            subscribed sessions before deleting your account, as these cannot be refunded once
            deleted.
          </p>
          <p className='text-sm text-[#C5C8D0]'>
            Deleting your account will also revoke access to your data and services across all
            platforms connected to FitStatic.
          </p>
        </div>
        <div className='flex flex-col gap-4 bg-bgPrimary rounded-xl p-4'>
          <Label className='text-xl font-semibold'>Tell us why you’re leaving</Label>
          <RadioGroup value={reason} onValueChange={handleReasonChange} className='space-y-4'>
            <div className='flex items-center space-x-2'>
              <RadioGroupItem
                value='too-many-emails'
                id='too-many-emails'
                className='border-white text-white'
              />
              <Label
                htmlFor='too-many-emails'
                className='text-sm font-normal text-white cursor-pointer'
              >
                I&apos;m receiving too many emails
              </Label>
            </div>
            <div className='flex items-center space-x-2'>
              <RadioGroupItem
                value='duplicate-account'
                id='duplicate-account'
                className='border-white text-white'
              />
              <Label
                htmlFor='duplicate-account'
                className='text-sm font-normal text-white cursor-pointer'
              >
                I accidentally created another account
              </Label>
            </div>
            <div className='flex items-center space-x-2'>
              <RadioGroupItem
                value='privacy-concerns'
                id='privacy-concerns'
                className='border-white text-white'
              />
              <Label
                htmlFor='privacy-concerns'
                className='text-sm font-normal text-white cursor-pointer'
              >
                I&apos;m concerned about privacy
              </Label>
            </div>
            <div className='flex items-center space-x-2'>
              <RadioGroupItem
                value='other'
                id='other'
                className='border-white text-white cursor-pointer'
              />
              <Label htmlFor='other' className='text-sm font-normal text-white'>
                Other
              </Label>
            </div>
          </RadioGroup>
        </div>
        {(error === 'type1' || error === 'type3') && (
          <p
            aria-label='error'
            aria-errormessage={'Select a reason'}
            className='text-red text-xs'
            style={{ marginTop: '5px' }}
          >
            Select a reason
          </p>
        )}
        <div className='flex flex-col gap-3'>
          <h4 className='text-lg font-semibold'>
            Deleting your account is permanent and irreversible
          </h4>
          <div className='flex items-center gap-3'>
            <Switch
              checked={confirm}
              onCheckedChange={(val) => {
                setConfirm(val);
                if (!reason && val) {
                  setError('type1');
                } else if (!val && reason) {
                  setError('type2');
                } else if (!val && !reason) {
                  setError('type3');
                } else {
                  setError('');
                }
              }}
            />
            <h5>I understand</h5>
          </div>
          {(error === 'type2' || error === 'type3') && (
            <p
              aria-label='error'
              aria-errormessage={'Select a reason'}
              className='text-red text-xs'
            >
              Please confirm
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DeleteAccount;
